import React from 'react'
import {
  Card,
  CardContent,
  Typography,
  TextField,
  FormControl,
  FormControlLabel,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Grid,
  Box,
  Chip,
  Collapse,
  Link,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Switch,
  Stack,
} from '@mui/material'
import VpnKeyIcon from '@mui/icons-material/VpnKey'
import LockIcon from '@mui/icons-material/Lock'
import SettingsIcon from '@mui/icons-material/Settings'

const AwsStorageOptionsStep = ({ formData, setFormData, isComplete, stepNumber = 6 }) => {

  const accountNumber = formData.awsAdditionalFields.accountNumber || ''
  const arnPrefix1 = accountNumber
    ? `arn:aws:acm:us-east-1:${accountNumber}:certificate/`
    : 'arn:aws:acm:us-east-1:<account>:certificate/'

  // Extract just the UUID portion from a full ARN (or return as-is if already just a UUID)
  const toUuid = (val) => {
    if (!val) return ''
    const idx = val.lastIndexOf('/')
    return idx !== -1 ? val.slice(idx + 1) : val
  }

  // Build full ARN from prefix + uuid
  const toFullArn = (prefix, uuid) => uuid ? `${prefix}${uuid}` : ''

  return (
    <Collapse in={formData.mutualAuth !== ''}>
      <Card variant="outlined" sx={{ mb: 3, borderRadius: 2 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip 
              label={isComplete ? "✓" : String(stepNumber)} 
              color={isComplete ? "success" : "primary"} 
              size="small" 
            />
            AWS Certificate Storage Options
          </Typography>
          <Divider sx={{ my: 2 }} />
          
          {/* AWS cert in OnPrem Toggle */}
          <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.awsAdditionalFields.awsCertInOnprem === 'yes'}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    awsAdditionalFields: { 
                      ...formData.awsAdditionalFields, 
                      awsCertInOnprem: e.target.checked ? 'yes' : 'no' 
                    } 
                  })}
                  color="primary"
                />
              }
              label={
                <Box>
                  <Typography variant="body1" fontWeight="medium">Certificate also used on-premises</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Enable if this AWS certificate is also deployed to on-premises infrastructure
                  </Typography>
                </Box>
              }
              sx={{ m: 0, alignItems: 'flex-start' }}
            />
          </Box>

          {/* Storage Options Selection Cards */}
          <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ mb: 2 }}>
            Select where to store certificate materials (select all that apply):
          </Typography>

          <Stack spacing={2}>
            {/* ACM Card */}
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                border: formData.awsAdditionalFields.acmParameter === 'yes' ? '2px solid' : '1px solid',
                borderColor: formData.awsAdditionalFields.acmParameter === 'yes' ? 'success.main' : 'grey.300',
                bgcolor: formData.awsAdditionalFields.acmParameter === 'yes' ? 'success.50' : 'background.paper',
                transition: 'all 0.2s ease',
              }}
            >
              {/* Clickable header row */}
              <Box
                sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%', pr: 2, cursor: 'pointer', '&:hover': { opacity: 0.85 } }}
                onClick={() => setFormData({
                  ...formData,
                  awsAdditionalFields: {
                    ...formData.awsAdditionalFields,
                    acmParameter: formData.awsAdditionalFields.acmParameter === 'yes' ? 'no' : 'yes',
                  },
                })}
              >
                <VpnKeyIcon sx={{ color: formData.awsAdditionalFields.acmParameter === 'yes' ? 'success.main' : 'grey.400', fontSize: 32 }} />
                <Box sx={{ flexGrow: 1 }}>
                  <Typography variant="subtitle1" fontWeight="medium">AWS Certificate Manager (ACM)</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Import certificate for use with AWS services like ALB, CloudFront, API Gateway
                  </Typography>
                </Box>
                <Chip
                  label={formData.awsAdditionalFields.acmParameter === 'yes' ? 'Enabled' : 'Enable'}
                  size="small"
                  color={formData.awsAdditionalFields.acmParameter === 'yes' ? 'success' : 'default'}
                  variant={formData.awsAdditionalFields.acmParameter === 'yes' ? 'filled' : 'outlined'}
                  sx={{ cursor: 'pointer' }}
                />
              </Box>

              {/* Existing ARN inputs — only for pre-existing certs when ACM is enabled */}
              <Collapse in={formData.awsAdditionalFields.acmParameter === 'yes' && formData.preExisting === 'true'}>
                <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'success.200' }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                    This certificate already exists in ACM. Enter its current ARN(s) so the client Lambda can update them in-place rather than creating duplicate entries. ARN 1 is required — you can provide a second ARN for a second region (optional).
                    {' '}To find the Certificate ID for your domain, search the{' '}
                    <Link
                      href="https://portal.csgportal.csgss.prd.prd.bfsaws.net/inventory/certificates"
                      target="_blank"
                      rel="noopener noreferrer"
                      variant="caption"
                    >
                      CSG Certificate Inventory
                    </Link>.
                  </Typography>
                  <Grid container spacing={2}>
                    {/* ARN 1 */}
                    <Grid item xs={12}>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, fontFamily: 'monospace' }}>
                        {arnPrefix1}
                      </Typography>
                      <TextField
                        fullWidth
                        size="small"
                        label="Certificate ID (ARN 1) *"
                        placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                        error={formData.awsAdditionalFields.acmParameter === 'yes' && formData.preExisting === 'true' && !toUuid(formData.awsAdditionalFields.existingAcmArn1)}
                        helperText={formData.awsAdditionalFields.acmParameter === 'yes' && formData.preExisting === 'true' && !toUuid(formData.awsAdditionalFields.existingAcmArn1) ? 'Required for pre-existing certificates' : ''}
                        value={toUuid(formData.awsAdditionalFields.existingAcmArn1)}
                        onChange={(e) => setFormData({
                          ...formData,
                          awsAdditionalFields: {
                            ...formData.awsAdditionalFields,
                            existingAcmArn1: toFullArn(arnPrefix1, e.target.value),
                          },
                        })}
                      />
                    </Grid>
                    {/* ARN 2 */}
                    <Grid item xs={12}>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, fontFamily: 'monospace' }}>
                        arn:aws:acm:us-west-2:{accountNumber || '<account>'}:certificate/
                      </Typography>
                      <TextField
                        fullWidth
                        size="small"
                        label="Certificate ID (ARN 2 — optional)"
                        placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                        value={toUuid(formData.awsAdditionalFields.existingAcmArn2)}
                        onChange={(e) => {
                          const prefix2 = `arn:aws:acm:us-west-2:${accountNumber || '<account>'}:certificate/`
                          setFormData({
                            ...formData,
                            awsAdditionalFields: {
                              ...formData.awsAdditionalFields,
                              existingAcmArn2: toFullArn(prefix2, e.target.value),
                            },
                          })
                        }}
                      />
                    </Grid>
                  </Grid>
                </Box>
              </Collapse>
            </Paper>

            {/* SSM Accordion */}
            <Accordion 
              expanded={formData.awsAdditionalFields.ssmParameter === 'yes'}
              onChange={(e, expanded) => setFormData({ 
                ...formData, 
                awsAdditionalFields: { 
                  ...formData.awsAdditionalFields, 
                  ssmParameter: expanded ? 'yes' : 'no' 
                } 
              })}
              sx={{ 
                border: formData.awsAdditionalFields.ssmParameter === 'yes' ? '2px solid' : '1px solid',
                borderColor: formData.awsAdditionalFields.ssmParameter === 'yes' ? 'info.main' : 'grey.300',
                borderRadius: '8px !important',
                '&:before': { display: 'none' },
                boxShadow: 'none'
              }}
            >
              <AccordionSummary 
                sx={{ 
                  bgcolor: formData.awsAdditionalFields.ssmParameter === 'yes' ? 'info.50' : 'background.paper',
                  borderRadius: '8px',
                  '& .MuiAccordionSummary-expandIconWrapper': { display: 'none' }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%', pr: 2 }}>
                  <SettingsIcon sx={{ color: formData.awsAdditionalFields.ssmParameter === 'yes' ? 'info.main' : 'grey.400', fontSize: 32 }} />
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="subtitle1" fontWeight="medium">SSM Parameter Store</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Store certificate components as encrypted parameters
                    </Typography>
                  </Box>
                  <Chip 
                    label={formData.awsAdditionalFields.ssmParameter === 'yes' ? 'Enabled' : 'Enable'} 
                    size="small" 
                    color={formData.awsAdditionalFields.ssmParameter === 'yes' ? 'info' : 'default'}
                    variant={formData.awsAdditionalFields.ssmParameter === 'yes' ? 'filled' : 'outlined'}
                    sx={{ cursor: 'pointer' }}
                  />
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ bgcolor: 'grey.50', pt: 3 }}>
                <Grid container spacing={2}>
                  {/* Format Selection */}
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                      {['PEM', 'PFX'].map((fmt) => (
                        <Chip
                          key={fmt}
                          label={fmt === 'PFX' ? 'PFX/PKCS12' : fmt}
                          onClick={() => {
                            setFormData(prev => {
                              const fmtLower = fmt.toLowerCase()
                              const sdlc = prev.environment ? prev.environment.toLowerCase() : ''
                              const certPath = prev.commonName && sdlc ? `/venafi/certificates/${sdlc}/${prev.commonName}/${fmtLower}/cert` : ''
                              const privateKeyPath = fmt === 'PEM' && prev.commonName && sdlc ? `/venafi/certificates/${sdlc}/${prev.commonName}/${fmtLower}/key` : ''
                              const chainKeyPath = fmt === 'PEM' && prev.commonName && sdlc ? `/venafi/certificates/${sdlc}/${prev.commonName}/${fmtLower}/chain` : ''
                              const passwordPath = fmt === 'PFX' && prev.commonName && sdlc ? `/venafi/certificates/${sdlc}/${prev.commonName}/${fmtLower}/password` : ''
                              return { 
                                ...prev, 
                                awsAdditionalFields: { 
                                  ...prev.awsAdditionalFields, 
                                  ssmFormat: fmt,
                                  ssmCertPath: certPath,
                                  ssmPrivateKeyPath: privateKeyPath,
                                  ssmChainKeyPath: chainKeyPath,
                                  ssmCertPasswordPath: passwordPath
                                } 
                              }
                            })
                          }}
                          color={formData.awsAdditionalFields.ssmFormat === fmt ? 'primary' : 'default'}
                          variant={formData.awsAdditionalFields.ssmFormat === fmt ? 'filled' : 'outlined'}
                          sx={{ px: 2, py: 2.5, fontSize: '0.95rem' }}
                        />
                      ))}
                    </Box>
                  </Grid>

                  {formData.awsAdditionalFields.ssmFormat && (
                    <>
                      {/* Certificate Path */}
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Certificate Path"
                          value={formData.awsAdditionalFields.ssmCertPath}
                          onChange={(e) => setFormData({ ...formData, awsAdditionalFields: { ...formData.awsAdditionalFields, ssmCertPath: e.target.value } })}
                          helperText="Auto-filled based on common name"
                        />
                      </Grid>

                      {/* Certificate Type Options */}
                      <Grid item xs={6}>
                        <FormControl size="small" fullWidth>
                          <InputLabel>Content Type</InputLabel>
                          <Select
                            value={formData.awsAdditionalFields.ssmCertContentType}
                            onChange={(e) => setFormData({ ...formData, awsAdditionalFields: { ...formData.awsAdditionalFields, ssmCertContentType: e.target.value } })}
                            label="Content Type"
                          >
                            <MenuItem value="String">String</MenuItem>
                            <MenuItem value="SecureString">SecureString</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={6}>
                        <FormControl size="small" fullWidth>
                          <InputLabel>Parameter Type</InputLabel>
                          <Select
                            value={formData.awsAdditionalFields.ssmCertParamType}
                            onChange={(e) => setFormData({ ...formData, awsAdditionalFields: { ...formData.awsAdditionalFields, ssmCertParamType: e.target.value } })}
                            label="Parameter Type"
                          >
                            {formData.awsAdditionalFields.ssmFormat === 'PEM' && <MenuItem value="Standard">Standard</MenuItem>}
                            <MenuItem value="Advanced">Advanced</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>

                      {/* PEM-specific fields */}
                      {formData.awsAdditionalFields.ssmFormat === 'PEM' && (
                        <>
                          <Grid item xs={12}>
                            <Divider sx={{ my: 1 }}><Typography variant="caption" color="text.secondary">Private Key</Typography></Divider>
                          </Grid>
                          <Grid item xs={12}>
                            <TextField
                              fullWidth
                              size="small"
                              label="Private Key Path"
                              value={formData.awsAdditionalFields.ssmPrivateKeyPath}
                              onChange={(e) => setFormData({ ...formData, awsAdditionalFields: { ...formData.awsAdditionalFields, ssmPrivateKeyPath: e.target.value } })}
                            />
                          </Grid>
                          <Grid item xs={6}>
                            <FormControl size="small" fullWidth>
                              <InputLabel>Content Type</InputLabel>
                              <Select
                                value={formData.awsAdditionalFields.ssmKeyContentType}
                                onChange={(e) => setFormData({ ...formData, awsAdditionalFields: { ...formData.awsAdditionalFields, ssmKeyContentType: e.target.value } })}
                                label="Content Type"
                              >
                                <MenuItem value="String">String</MenuItem>
                                <MenuItem value="SecureString">SecureString</MenuItem>
                              </Select>
                            </FormControl>
                          </Grid>
                          <Grid item xs={6}>
                            <FormControl size="small" fullWidth>
                              <InputLabel>Parameter Type</InputLabel>
                              <Select
                                value={formData.awsAdditionalFields.ssmKeyParamType}
                                onChange={(e) => setFormData({ ...formData, awsAdditionalFields: { ...formData.awsAdditionalFields, ssmKeyParamType: e.target.value } })}
                                label="Parameter Type"
                              >
                                <MenuItem value="Standard">Standard</MenuItem>
                                <MenuItem value="Advanced">Advanced</MenuItem>
                              </Select>
                            </FormControl>
                          </Grid>

                          <Grid item xs={12}>
                            <Divider sx={{ my: 1 }}><Typography variant="caption" color="text.secondary">Certificate Chain</Typography></Divider>
                          </Grid>
                          <Grid item xs={12}>
                            <TextField
                              fullWidth
                              size="small"
                              label="Chain Path"
                              value={formData.awsAdditionalFields.ssmChainKeyPath}
                              onChange={(e) => setFormData({ ...formData, awsAdditionalFields: { ...formData.awsAdditionalFields, ssmChainKeyPath: e.target.value } })}
                            />
                          </Grid>
                          <Grid item xs={6}>
                            <FormControl size="small" fullWidth>
                              <InputLabel>Content Type</InputLabel>
                              <Select
                                value={formData.awsAdditionalFields.ssmChainContentType}
                                onChange={(e) => setFormData({ ...formData, awsAdditionalFields: { ...formData.awsAdditionalFields, ssmChainContentType: e.target.value } })}
                                label="Content Type"
                              >
                                <MenuItem value="String">String</MenuItem>
                                <MenuItem value="SecureString">SecureString</MenuItem>
                              </Select>
                            </FormControl>
                          </Grid>
                          <Grid item xs={6}>
                            <FormControl size="small" fullWidth>
                              <InputLabel>Parameter Type</InputLabel>
                              <Select
                                value={formData.awsAdditionalFields.ssmChainParamType}
                                onChange={(e) => setFormData({ ...formData, awsAdditionalFields: { ...formData.awsAdditionalFields, ssmChainParamType: e.target.value } })}
                                label="Parameter Type"
                              >
                                <MenuItem value="Advanced">Advanced</MenuItem>
                              </Select>
                            </FormControl>
                          </Grid>
                        </>
                      )}

                      {/* PFX-specific fields */}
                      {formData.awsAdditionalFields.ssmFormat === 'PFX' && (
                        <>
                          <Grid item xs={12}>
                            <Divider sx={{ my: 1 }}><Typography variant="caption" color="text.secondary">Password</Typography></Divider>
                          </Grid>
                          <Grid item xs={12}>
                            <TextField
                              fullWidth
                              size="small"
                              label="Password Path"
                              value={formData.awsAdditionalFields.ssmCertPasswordPath}
                              onChange={(e) => setFormData({ ...formData, awsAdditionalFields: { ...formData.awsAdditionalFields, ssmCertPasswordPath: e.target.value } })}
                            />
                          </Grid>
                          <Grid item xs={6}>
                            <FormControl size="small" fullWidth>
                              <InputLabel>Content Type</InputLabel>
                              <Select
                                value={formData.awsAdditionalFields.ssmPasswordContentType}
                                onChange={(e) => setFormData({ ...formData, awsAdditionalFields: { ...formData.awsAdditionalFields, ssmPasswordContentType: e.target.value } })}
                                label="Content Type"
                              >
                                <MenuItem value="String">String</MenuItem>
                                <MenuItem value="SecureString">SecureString</MenuItem>
                              </Select>
                            </FormControl>
                          </Grid>
                          <Grid item xs={6}>
                            <FormControl size="small" fullWidth>
                              <InputLabel>Parameter Type</InputLabel>
                              <Select
                                value={formData.awsAdditionalFields.ssmPasswordParamType}
                                onChange={(e) => setFormData({ ...formData, awsAdditionalFields: { ...formData.awsAdditionalFields, ssmPasswordParamType: e.target.value } })}
                                label="Parameter Type"
                              >
                                <MenuItem value="Standard">Standard</MenuItem>
                                <MenuItem value="Advanced">Advanced</MenuItem>
                              </Select>
                            </FormControl>
                          </Grid>
                        </>
                      )}
                    </>
                  )}
                </Grid>
              </AccordionDetails>
            </Accordion>

            {/* Secrets Manager Accordion */}
            <Accordion 
              expanded={formData.awsAdditionalFields.smParameter === 'yes'}
              onChange={(e, expanded) => setFormData({ 
                ...formData, 
                awsAdditionalFields: { 
                  ...formData.awsAdditionalFields, 
                  smParameter: expanded ? 'yes' : 'no' 
                } 
              })}
              sx={{ 
                border: formData.awsAdditionalFields.smParameter === 'yes' ? '2px solid' : '1px solid',
                borderColor: formData.awsAdditionalFields.smParameter === 'yes' ? 'warning.main' : 'grey.300',
                borderRadius: '8px !important',
                '&:before': { display: 'none' },
                boxShadow: 'none'
              }}
            >
              <AccordionSummary 
                sx={{ 
                  bgcolor: formData.awsAdditionalFields.smParameter === 'yes' ? 'warning.50' : 'background.paper',
                  borderRadius: '8px',
                  '& .MuiAccordionSummary-expandIconWrapper': { display: 'none' }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%', pr: 2 }}>
                  <LockIcon sx={{ color: formData.awsAdditionalFields.smParameter === 'yes' ? 'warning.main' : 'grey.400', fontSize: 32 }} />
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="subtitle1" fontWeight="medium">Secrets Manager</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Store certificate as encrypted secret with automatic rotation support
                    </Typography>
                  </Box>
                  <Chip 
                    label={formData.awsAdditionalFields.smParameter === 'yes' ? 'Enabled' : 'Enable'} 
                    size="small" 
                    color={formData.awsAdditionalFields.smParameter === 'yes' ? 'warning' : 'default'}
                    variant={formData.awsAdditionalFields.smParameter === 'yes' ? 'filled' : 'outlined'}
                    sx={{ cursor: 'pointer' }}
                  />
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ bgcolor: 'grey.50', pt: 3 }}>
                <Grid container spacing={2}>
                  {/* Format Selection */}
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                      {['PEM', 'PFX', 'JKS'].map((fmt) => (
                        <Chip
                          key={fmt}
                          label={fmt === 'PFX' ? 'PFX/PKCS12' : fmt}
                          onClick={() => {
                            setFormData(prev => {
                              const sdlc = prev.environment ? prev.environment.toLowerCase() : ''
                              const certPath = prev.commonName && sdlc ? `/venafi/certificates/${sdlc}/${prev.commonName}` : ''
                              return { 
                                ...prev, 
                                awsAdditionalFields: { 
                                  ...prev.awsAdditionalFields, 
                                  smFormat: fmt,
                                  smCertPath: certPath,
                                } 
                              }
                            })
                          }}
                          color={formData.awsAdditionalFields.smFormat === fmt ? 'primary' : 'default'}
                          variant={formData.awsAdditionalFields.smFormat === fmt ? 'filled' : 'outlined'}
                          sx={{ px: 2, py: 2.5, fontSize: '0.95rem' }}
                        />
                      ))}
                    </Box>
                  </Grid>

                  {formData.awsAdditionalFields.smFormat && (
                    <>
                      {/* Certificate Path */}
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Secret Name / Path"
                          value={formData.awsAdditionalFields.smCertPath}
                          onChange={(e) => setFormData({ ...formData, awsAdditionalFields: { ...formData.awsAdditionalFields, smCertPath: e.target.value } })}
                          helperText="Auto-filled based on common name"
                        />
                      </Grid>

                      {/* Info: one secret is created containing all keys for the selected format */}
                      {formData.awsAdditionalFields.smFormat && (
                        <Grid item xs={12}>
                          <Typography variant="caption" color="text.secondary">
                            {formData.awsAdditionalFields.smFormat === 'PEM'
                              ? 'One secret will be created with keys: certificate, private_key, chain'
                              : 'One secret will be created with keys: certificate, password'}
                          </Typography>
                        </Grid>
                      )}
                    </>
                  )}
                </Grid>
              </AccordionDetails>
            </Accordion>
          </Stack>
        </CardContent>
      </Card>
    </Collapse>
  )
}

export default AwsStorageOptionsStep
